import { useMemo } from 'react';
import {
  getCandidateInfo,
  getCandidateActivities,
  getCandidateCertifications,
  getCandidateEducations,
  getCandidateFiles,
  getCandidateITSkills,
  getCandidateLanguages,
  getCandidateSocialMedia,
  getCandidateSocialMediaExpertises,
  getCandidateWorkExperience
} from 'redux/slices/candidate';
import { LANGUAGES } from 'constants/constants';
import { CandidateContactCard } from './CandidateContactCard';
import { CandidatePreferencesCard } from './CandidatePreferencesCard';
import { CandidateModalCard } from './CandidateModalCard';
import { CandidatePitchCard } from './CandidatePitchCard';
import { CandidateNotes } from './CandidateNotes';
import { CandidateSkillsCard } from './CandidateSkillsCard';
import { CandidateMindgageCard } from './CandidateMindgageCard';

interface CandidateProfileCardsProps {
  // Core flags
  token: string | null;
  isForCostumerProfile?: boolean;
  isEmployee: boolean;
  isCandidate: boolean;
  isRecruiter: boolean;
  isAdmin: boolean;
  recruiterOrAdmin: boolean;
  isCandidateProfile: boolean;
  isMadeAnonymous: boolean;

  // Data
  candidate: any;
  customer: any;
  notes: any;
  files: any;
  modules: any;
  company: any;

  // IDs
  candidateID: string | number;
  customerId?: string | number;

  // Translation and editing
  t: (key: string) => string;
  isEditable: boolean;

  // Skills related
  editSkillsModalOpen: boolean;
  handleSkillsModalToggle: (open: boolean) => void;
  skillsStreaming: boolean;
  skillsProgress: any;
  isCvUploadModal: boolean;
  hard_skills: string[];
  soft_skills: string[];

  // Actions
  createOnInit: (dispatchAction: any, condition?: boolean) => () => void;
}

interface CandidateProfileCardsResult {
  cards: JSX.Element[];
  cardsCount: JSX.Element[];
  workExperiences: JSX.Element | null;
}

/**
 * Generates all candidate profile cards based on exact conditions and order
 * specified in CandidateProfile_CardRendering_Specification.md
 */
export const useCandidateProfileCards = (
  props: CandidateProfileCardsProps
): CandidateProfileCardsResult => {
  const {
    token,
    isForCostumerProfile,
    isEmployee,
    isCandidate,
    isRecruiter,
    isAdmin,
    recruiterOrAdmin,
    isCandidateProfile,
    isMadeAnonymous,
    candidate,
    customer,
    notes,
    files,
    modules,
    company,
    candidateID,
    customerId,
    t,
    isEditable,
    editSkillsModalOpen,
    handleSkillsModalToggle,
    skillsStreaming,
    skillsProgress,
    isCvUploadModal,
    hard_skills,
    soft_skills,
    createOnInit
  } = props;

  return useMemo(() => {
    const cards: JSX.Element[] = [];
    const cardsCount: JSX.Element[] = [];

    // Calculate isAnonymous once
    const isAnonymous = !recruiterOrAdmin && !isCandidateProfile && !!token;

    // 1. Contact Card (Order: 1)
    // Condition: !token || isForCostumerProfile
    // Added to: cards only (NOT cardsCount)
    if (!token || isForCostumerProfile) {
      cards.push(
        <CandidateContactCard
          key="contact"
          title={!isForCostumerProfile ? t('Contact info') : t('Info')}
          mainInfo={!isForCostumerProfile ? candidate.mainInfo : customer}
          recruiterOrAdmin={recruiterOrAdmin}
          language={
            !modules.preference && candidate.mainInfo?.default_lang
              ? LANGUAGES[Number(candidate.mainInfo.default_lang) - 1].label
              : ''
          }
          isForCustomer={isForCostumerProfile}
          customerId={String(customerId || '')}
          customer={customer}
          calendar
        />
      );
    }

    // 2. IT Skills (Order: 2)
    // Condition: modules.it_skill && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.it_skill && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="it-skills"
          title={t('IT Skills')}
          type="it-skills"
          candidateId={candidateID}
          editable={isEditable}
          dataList={candidate.it_skill}
          onInit={createOnInit(getCandidateITSkills, modules.it_skill)}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 3. Skills (Order: 3)
    // Condition: !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (!isEmployee) {
      const component = (
        <CandidateSkillsCard
          key="skills"
          title={t('Skills')}
          type="skills"
          isCandidate={isCandidate}
          candidateId={String(candidateID)}
          editSkillsModalOpen={editSkillsModalOpen}
          setEditSkillsModalOpen={handleSkillsModalToggle}
          initialHardSkills={{
            title: 'hard_skills',
            data: candidate.mainInfo?.hard_skills || []
          }}
          initialSoftSkills={{
            title: 'soft_skills',
            data: candidate.mainInfo?.soft_skills || []
          }}
          hardSkills={{ title: 'hard_skills', data: hard_skills || [] }}
          softSkills={{ title: 'soft_skills', data: soft_skills || [] }}
          isEditable={isEditable}
          onInit={createOnInit(getCandidateInfo)}
          skillsStreaming={skillsStreaming}
          skillsProgress={skillsProgress}
          isCvUploadModal={isCvUploadModal}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 4. Activities (Order: 4)
    // Condition: modules.extra && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.extra && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="activities"
          title={t('Activities')}
          type="activities"
          candidateId={candidateID}
          editable={isEditable}
          dataList={candidate.activities}
          onInit={createOnInit(getCandidateActivities, modules.extra)}
          iconSrc="/static/img/profile/activities.svg"
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 5. Pitch (Order: 5)
    // Condition: modules.pitch && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.pitch && !isEmployee) {
      const component = (
        <CandidatePitchCard
          key="pitch"
          candidateId={candidateID}
          editable={isEditable}
          mainInfo={candidate.mainInfo || null}
          candidate={true}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 6. Certifications (Order: 6)
    // Condition: modules.certification && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.certification && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="certifications"
          title={t('Certifications')}
          type="certifications"
          candidateId={candidateID}
          editable={isEditable}
          dataList={candidate.certifications}
          onInit={createOnInit(
            getCandidateCertifications,
            modules.certification
          )}
          iconSrc="/static/img/profile/certified.svg"
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 7. Notes (Order: 7)
    // Condition: (isRecruiter || isAdmin || token) && (!token || isForCostumerProfile)
    // Added to: cards only (NOT cardsCount)
    if ((isRecruiter || isAdmin || token) && (!token || isForCostumerProfile)) {
      cards.push(
        <CandidateNotes
          key="notes"
          addNoteTitle={
            isForCostumerProfile
              ? t('Notes')
              : t('Add your notes for this candidate')
          }
          title={t('Candidate Log')}
          candidateId={isForCostumerProfile ? customerId : candidateID}
          dataList={isForCostumerProfile ? notes : candidate.notes}
          type="notes"
          mainInfo={isForCostumerProfile ? customer : candidate?.mainInfo}
          isAnonymous={isAnonymous}
          iconSrc="/static/img/profile/notes.svg"
          isForCostumerProfile={isForCostumerProfile}
          customerId={customerId}
        />
      );
    }

    // 8. Preferences (Order: 8)
    // Condition: modules.preference && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.preference && !isEmployee) {
      const component = (
        <CandidatePreferencesCard
          key="preferences"
          candidateId={candidateID}
          editable={isEditable}
          mainInfo={candidate.mainInfo || null}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 9. Social Media (Order: 9)
    // Condition: modules.social_media && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.social_media && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="social-media"
          title={t('Social Media')}
          type="social-media"
          candidateId={candidateID}
          editable={isEditable}
          dataList={!isMadeAnonymous ? candidate.social_media || '' : ''}
          onInit={createOnInit(getCandidateSocialMedia, modules.social_media)}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 10. Social Media Expertises (Order: 10)
    // Condition: modules.social_media_expertise && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.social_media_expertise && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="social-media-expertises"
          title={t('Social Media Expertises')}
          type="social-media-expertises"
          candidateId={candidateID}
          editable={isEditable}
          dataList={candidate.social_media_expertise}
          onInit={createOnInit(
            getCandidateSocialMediaExpertises,
            modules.social_media_expertise
          )}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 11. Languages (Order: 11)
    // Condition: modules.language && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.language && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="languages"
          title={t('Languages')}
          type="languages"
          candidateId={candidateID}
          editable={isEditable}
          dataList={candidate.languages}
          onInit={createOnInit(getCandidateLanguages, modules.language)}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    const mindgageEnabled = true
      // company?.enable_mindgage === true ||
      // (company?.enable_mindgage !== false && candidate?.mindgage !== undefined);

    if (mindgageEnabled && !isEmployee) {
      const component = (
        <CandidateMindgageCard
          key="mindgage"
          title={t('Mindgage')}
          candidate={candidate}
          mindgage={candidate?.mainInfo?.mindgage}
          iconSrc="/static/img/profile/professional-skills.svg"
        />
      );
      cardsCount.push(component);
      cards.push(component);
    }

    // 12. Upload Files (Order: 12)
    // Condition: !isEmployee && !token
    // Added to: cards only (NOT cardsCount)
    if (!isEmployee && !token) {
      cards.push(
        <CandidateModalCard
          key="upload-files"
          title={t('Upload Files')}
          type="upload_files"
          candidateId={isForCostumerProfile ? customerId : candidateID}
          editable={isEditable}
          dataList={isForCostumerProfile ? files : candidate.files}
          onInit={createOnInit(getCandidateFiles, !token)}
          isForCostumerProfile={isForCostumerProfile}
          customerId={customerId}
        />
      );
    }

    // 13. Educations (Order: 13)
    // Condition: modules.education && !isEmployee && !isForCostumerProfile
    // Added to: both cards and cardsCount (if !isForCostumerProfile)
    if (modules.education && !isEmployee) {
      const component = (
        <CandidateModalCard
          key="educations"
          title={t('Educations')}
          type="educations"
          candidateId={candidateID}
          editable={isEditable}
          dataList={candidate.educations}
          onInit={createOnInit(getCandidateEducations, modules.education)}
        />
      );
      if (!isForCostumerProfile) {
        cardsCount.push(component);
        cards.push(component);
      }
    }

    // 14. Work Experiences (Order: 14)
    // Condition: modules.work_experience && !isEmployee && !isForCostumerProfile
    // Added to: cards (if condition met), handled separately for PDF
    const workExperiences =
      modules.work_experience && !isEmployee && !isForCostumerProfile
        ? {
            id: 'work-experiences',
            component: (
              <CandidateModalCard
                key="work-experiences"
                title={t('Work Experiences')}
                type="work-experiences"
                candidateId={candidateID}
                editable={isEditable}
                dataList={candidate.work_experience}
                onInit={createOnInit(
                  getCandidateWorkExperience,
                  modules.work_experience
                )}
              />
            )
          }
        : null;

    if (workExperiences) {
      cards.push(workExperiences.component);
    }

    return {
      cards,
      cardsCount,
      workExperiences: workExperiences?.component || null
    };
  }, [
    token,
    isForCostumerProfile,
    isEmployee,
    isCandidate,
    isRecruiter,
    isAdmin,
    recruiterOrAdmin,
    isCandidateProfile,
    isMadeAnonymous,
    candidate,
    customer,
    notes,
    files,
    modules,
    company,
    candidateID,
    customerId,
    t,
    isEditable,
    editSkillsModalOpen,
    handleSkillsModalToggle,
    skillsStreaming,
    skillsProgress,
    isCvUploadModal,
    hard_skills,
    soft_skills,
    createOnInit
  ]);
};
